<template>
    <div class="vehicle-pricing" :class="'vehicle-pricing--' + mode">
        <LoadingIndicator v-if="isLoading || !pricing">
            <template v-if="!totalOnly">{{ $t('pricing.loading') }}</template>
        </LoadingIndicator>
        <Notice v-else-if="loadPricingError" type="warning">
            {{ $t('errors.an_error_occurred') }}
            {{ $t('errors.try_again_or_contact_us') }}
        </Notice>
        <template v-else-if="totalOnly && pricing.totalPrice">
            {{ pricing.totalPrice | price | formatAsPrice(pricing.currency, decimalPlaces) }}
        </template>
        <template v-else-if="totalOnlyWithAddons && pricing.totalPrice">
            {{ simulatedOrder.total | price | formatAsPrice(pricing.currency, decimalPlaces) }}
        </template>
        <template v-else-if="orderPricing">
            <OrderPricingTable
                v-if="simulatedOrder"
                :order="simulatedOrder"
                :order-items="simulatedOrderItems"
                :number-of-nights="numberOfNights"
                :is-baloise-insurance="isBaloiseInsurance"
                :is-omocom-insurance="isOmocomInsurance"
                :show-title="showTitle"
                :show-price-per-night="showPricePerNight"
            />
            <div v-if="pricing.deposit > 0 && showDeposit" class="deposit">
                <p class="deposit__title">{{ $t('pricing.deposit_title') }}</p>
                <p class="deposit__info">
                    {{ $t('pricing.deposit_footnote') }}
                    <a target="_blank" :href="$t('links.renter_deposit')">
                        {{ $t('common.read_more') }}
                    </a>
                </p>
                <client-only>
                    <table>
                        <tr>
                            <th>{{ $t('pricing.deposit') }}</th>
                            <td>{{ pricing.deposit | price | formatAsPrice(pricing.currency, 0) }}</td>
                        </tr>
                    </table>
                </client-only>
            </div>

            <div v-if="showConvertedPriceInfo && pricing.currency !== userCurrency" class="converted-price-info">
                <span>{{ $t('pricing.converted_info') }}</span>
                <svg-icon
                    v-tooltip="$t('pricing.converted_notice', { currency: vehicle.currency.toUpperCase() })"
                    name="info-outline"
                />
            </div>

            <div v-if="showDeposit" class="deposit">
                <p class="deposit__title">{{ $t('pricing.deposit_title') }}</p>
                <p>
                    {{ $t('pricing.cash_deposit_footnote') }}
                </p>
            </div>
        </template>
        <template v-else>
            <client-only>
                <table>
                    <tr v-if="simulatedOrder">
                        <th v-if="showNumOfNights">
                            {{ $tc('pricing.rental_fee_total_with_nights', numberOfNights, { numberOfNights }) }}
                        </th>
                        <th v-else>{{ $t('pricing.rental_fee_total') }}</th>
                        <td>{{ simulatedOrder.total | price | formatAsPrice(pricing.currency, 0) }}</td>
                    </tr>
                    <template v-if="pricing.totalPrice">
                        <tr class="included">
                            <th v-if="pricing.insuranceFee > 0">
                                <div class="vehicle-pricing__platform-insurance">
                                    <span>
                                        {{ $t('pages.booking_request.insurance_step.insurance_by') }}
                                    </span>
                                    <InsuranceLogo
                                        :vendor="vehicle.insuranceVendor"
                                        class="vehicle-pricing__platform-insurance-logo"
                                        :class="{
                                            'vehicle-pricing__platform-insurance-logo__omocom': isOmocomInsurance,
                                        }"
                                    />
                                </div>
                            </th>
                            <th v-else>{{ $t('pricing.owners_insurance') }}</th>
                            <td>{{ $t('common.included') }}</td>
                        </tr>
                        <tr class="included">
                            <th v-if="includedKilometers === UNLIMITED_KILOMETERS">
                                {{ $t('pricing.unlimited_kilometers') }}
                            </th>
                            <th v-else>
                                {{ $t('pricing.first_km', { kilometers: includedKilometers }) }}
                            </th>
                            <td>{{ $t('common.included') }}</td>
                        </tr>
                    </template>
                </table>
            </client-only>
        </template>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import LoadingIndicator from '~/components/LoadingIndicator.vue';
import Notice from '~/components/Notice.vue';
import OrderPricingTable from '~/components/booking/OrderPricingTable.vue';
import InsuranceLogo from '~/components/InsuranceLogo.vue';
import {
    ADDITIONAL_DRIVER,
    INSURANCE_ADDON,
    INSURANCE_ADDON_TYPE,
    PLATFORM_INSURANCE,
    CLEANING,
    PRICING_STRATEGY_PER_BOOKING,
    PRICING_STRATEGY_PER_BOOKING_NIGHT,
    TYPE_EQUIPMENT_ADDON,
    TYPE_SERVICE_ADDON,
    OMOCOM_COMPLETE,
    OMOCOM_PLUS,
    OMOCOM_BASIC,
} from '~/config/productTypes';
import { BALOISE, OMOCOM } from '~/config/insurance';
import { UNLIMITED_KILOMETERS } from '~/config/platformPricingConfig';

export default {
    components: {
        LoadingIndicator,
        Notice,
        OrderPricingTable,
        InsuranceLogo,
    },
    props: {
        vehicle: {
            type: Object,
            default: () => {},
        },
        fromDate: {
            type: String,
            default: null,
        },
        toDate: {
            type: String,
            default: null,
        },
        mode: {
            type: String,
            default: 'vehicle-pricing',
            validator(value) {
                return ['vehicle-pricing', 'total-only', 'order-pricing', 'total-only-with-addons'].includes(value);
            },
        },
        selectedAddons: {
            type: Array,
            default: () => [],
        },
        addonProducts: {
            type: Array,
            default: () => [],
        },
        pricing: {
            type: Object,
            default: null,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        loadPricingError: {
            type: Boolean,
            default: false,
        },
        showDeposit: {
            type: Boolean,
            default: true,
        },
        showNumOfNights: {
            type: Boolean,
            default: true,
        },
        showConvertedPriceInfo: {
            type: Boolean,
            default: true,
        },
        decimalPlaces: {
            type: Number,
            default: 0,
        },
        showTitle: {
            type: Boolean,
            default: true,
        },
        showPricePerNight: {
            type: Boolean,
            default: false,
        },
        includedKilometers: {
            type: Number,
            default: UNLIMITED_KILOMETERS,
        },
    },
    data() {
        return {
            UNLIMITED_KILOMETERS,
        };
    },
    computed: {
        ...mapGetters(['userCurrency']),
        totalOnly() {
            return this.mode === 'total-only';
        },
        totalOnlyWithAddons() {
            return this.mode === 'total-only-with-addons';
        },
        orderPricing() {
            return this.mode === 'order-pricing';
        },
        numberOfNights() {
            return this.pricing.numberOfNights;
        },
        simulatedOrder() {
            const { pricing } = this;

            if (!pricing.totalPrice) {
                return;
            }

            const serviceFee = pricing.serviceFee + this.addonsServiceFee;

            return {
                currency: pricing.currency,
                serviceFee,
                serviceFeeTaxPercentage: pricing.serviceFeeTaxPercentage,
                total: this.simulatedOrderTotal,
            };
        },
        simulatedOrderTotal() {
            const { pricing } = this;

            if (!pricing.totalPrice) {
                return 0;
            }

            // We have to subtract Omocom base product price since it is included in pricing.totalPrice
            if (this.selectedAddons?.some(addon => [OMOCOM_PLUS, OMOCOM_COMPLETE].includes(addon.key))) {
                const baseOmocomProduct = this.addonProducts.find(addon => addon.key === OMOCOM_BASIC);
                const baseOmocomProductPrice = baseOmocomProduct?.price || 0;

                return pricing.totalPrice + this.addonsTotal - baseOmocomProductPrice * pricing.numberOfNights;
            }

            return pricing.totalPrice + this.addonsTotal;
        },
        simulatedOrderItems() {
            const items = [];

            const addItem = (id, subTotal, productType, product = {}) => {
                if (subTotal > 0) {
                    items.push({ id, subTotal, productType, product });
                }
            };

            addItem('merged_nights', this.pricing.rentalNights, 'low_season_night', {
                description: this.$tc('pricing.regular_booking_nights', this.numberOfNights),
                quantity: this.numberOfNights,
            });

            addItem(CLEANING, this.pricing.cleaningFee, CLEANING);

            if (this.isOmocomInsurance) {
                const omocomPlusPrice = this.getSelectedAddonData(OMOCOM_PLUS, 'price');
                const omocomCompletePrice = this.getSelectedAddonData(OMOCOM_COMPLETE, 'price');

                addItem(OMOCOM_PLUS, omocomPlusPrice, PLATFORM_INSURANCE, { key: OMOCOM_PLUS });
                addItem(OMOCOM_COMPLETE, omocomCompletePrice, PLATFORM_INSURANCE, { key: OMOCOM_COMPLETE });

                if (!omocomPlusPrice && !omocomCompletePrice) {
                    addItem(PLATFORM_INSURANCE, this.pricing.insuranceFee, PLATFORM_INSURANCE);
                }
            } else {
                addItem(PLATFORM_INSURANCE, this.pricing.insuranceFee, PLATFORM_INSURANCE);
            }

            addItem(ADDITIONAL_DRIVER, this.getSelectedAddonData(ADDITIONAL_DRIVER, 'price'), ADDITIONAL_DRIVER);

            // Add owner addons
            const ownerAddonTypes = [TYPE_EQUIPMENT_ADDON, TYPE_SERVICE_ADDON];
            // Looping through selectedAddons (instead of addonProducts) so that the order is preserved
            // i.e. add-ons are shown in the same order both in the booking request form and in the order pricing table
            this.selectedAddons.forEach(selectedAddon => {
                const addon = this.addonProducts.find(a => a.key === selectedAddon.key);

                if (addon && ownerAddonTypes.includes(addon.type)) {
                    const quantity = selectedAddon.quantity || 1;
                    addon.selectedQuantity = quantity;
                    addItem(addon.key, this.getSelectedAddonData(addon.key, 'price', quantity), addon.type, addon);
                }
            });

            this.vehicle?.mandatoryAddonProducts?.forEach(addon => {
                addItem(addon.key, this.getProductData(addon, 'price'), addon.type, addon);
            });

            const insuranceAddons = [
                INSURANCE_ADDON.deductible_buydown,
                INSURANCE_ADDON.interior_insurance,
                INSURANCE_ADDON.roadside_assistance,
                INSURANCE_ADDON.luggage_insurance,
                INSURANCE_ADDON.cancellation_cost_insurance,
                INSURANCE_ADDON.young_driver,
                INSURANCE_ADDON.travel_abroad,
            ];

            insuranceAddons.forEach(addonKey => {
                addItem(addonKey, this.getSelectedAddonData(addonKey, 'price'), INSURANCE_ADDON_TYPE, {
                    key: addonKey,
                });
            });

            return items;
        },
        isBaloiseInsurance() {
            if (!this.vehicle.insuranceVendor?.name) {
                return false;
            }

            return this.vehicle.insuranceVendor.name.toLowerCase() === BALOISE;
        },
        isOmocomInsurance() {
            if (!this.vehicle.insuranceVendor?.name) {
                return false;
            }

            return this.vehicle.insuranceVendor.name.toLowerCase() === OMOCOM;
        },
        vehicleInsuranceCompany() {
            return this.vehicle.insuranceVendor?.name || this.vehicle.insuranceCompany;
        },
        addonsTotal() {
            return this.addonProducts.reduce((total, addon) => {
                const productKeyOrType = addon.type === ADDITIONAL_DRIVER ? addon.type : addon.key;
                const quantity = this.selectedAddons.find(a => a.key === productKeyOrType)?.quantity || 1;

                return total + this.getSelectedAddonData(productKeyOrType, 'price', quantity);
            }, 0);
        },
        addonsServiceFee() {
            return this.addonProducts.reduce((total, addon) => {
                const productKeyOrType = addon.type === ADDITIONAL_DRIVER ? addon.type : addon.key;
                const quantity = this.selectedAddons.find(a => a.key === productKeyOrType)?.quantity || 1;

                return total + this.getSelectedAddonData(productKeyOrType, 'serviceFee', quantity);
            }, 0);
        },
    },
    methods: {
        isSelectedAddon(productType) {
            const addon = this.selectedAddons.find(addon => addon.key === productType);

            return addon?.selected || false;
        },
        isMandatoryAddon(productType) {
            return this.vehicle.mandatoryAddons?.[productType] || false;
        },
        getAddon(productType) {
            return this.addonProducts.find(p => p.type === productType || p.key === productType);
        },
        getSelectedAddonData(productType, property, quantity = 1) {
            const product = this.getAddon(productType);

            if (!this.addonProducts || !product || !this.isSelectedAddon(productType)) {
                return 0;
            }

            return this.getProductData(product, property, quantity);
        },
        getProductData(product, property, quantity = 1) {
            if (!product) {
                return 0;
            }

            switch (product.pricingStrategy) {
                case PRICING_STRATEGY_PER_BOOKING_NIGHT:
                    return product[property] * this.numberOfNights * quantity;
                case PRICING_STRATEGY_PER_BOOKING:
                    return product[property] * quantity;
                default:
                    this.$sentry.withScope(scope => {
                        scope.setExtra('product', product);
                        this.$sentry.captureMessage(
                            `Unsupported pricing strategy for add-on product: ${product.pricingStrategy}`,
                            'error'
                        );
                    });

                    return product[property] * quantity;
            }
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/_variables.scss';

.vehicle-pricing {
    table {
        width: 100%;
        margin: auto;
    }

    tr {
        height: 30px;
    }

    .included {
        td,
        th {
            color: $primary;
        }
    }

    .deposit {
        border-top: 1px solid $border-color;
        margin-top: 20px;
        padding-top: 24px;

        &__title {
            font-weight: bold;
        }

        &__info {
            color: $secondary-text-color;
            margin-bottom: 24px;
        }
    }

    .converted-price-info {
        margin-top: 24px;
        padding-top: 24px;
        border-top: 1px solid $border-color;
    }

    td {
        text-align: right;
    }

    &__platform-insurance {
        display: flex;
        align-items: center;
        gap: 4px;
    }

    &__platform-insurance-logo {
        display: flex;
        width: 70px;
        height: 14px;
        margin-top: -1px;

        &__omocom {
            height: 26px;
            width: auto;
            margin-top: -14px;
        }
    }
}
</style>
